import type { TFunction } from 'i18next';
import { z } from 'zod';
import { LimitedCompanyLocation } from 'ogp/services/company-service.types';

const isLimitedCompanyLocation = (value: unknown): value is LimitedCompanyLocation =>
  typeof value === 'object' &&
  value !== null &&
  'address' in value &&
  typeof value.address === 'string' &&
  value.address.trim().length > 0;

export const createLimitedCompanySchema = (t: TFunction) => {
  const requiredMessage = t('form.validation.required', { ns: 'web-common' });
  const invalidEmailMessage = t('form.validation.invalid-email', { ns: 'web-common' });
  const invalidPhoneMessage = t('form.validation.invalid-phone', {
    ns: 'web-common',
    defaultValue: 'Voorbeeld: +31612345678',
  });

  return z.object({
    cocNumber: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .regex(
        /^\d{8}$/,
        t('collegial-lending.customers.create.validation.coc-number-invalid', { ns: 'web-common' })
      ),
    companyName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    location: z.custom<LimitedCompanyLocation>(isLimitedCompanyLocation, {
      message: requiredMessage,
    }),
    invoiceEmail: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .email(invalidEmailMessage),
    profileImageUrl: z.string(),
    adminFirstName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    adminLastName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    adminEmail: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .email(invalidEmailMessage),
    adminPhone: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .regex(/^\+[0-9]+$/, invalidPhoneMessage),
  });
};

export type CreateLimitedCompanyForm = z.infer<ReturnType<typeof createLimitedCompanySchema>>;
